import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { useCourses } from '../context/CourseContext';
import { useTemplates } from '../context/TemplateContext';
import { useInstances } from '../context/InstanceContext';
import { USER_ROLES } from '../config/userRoles';
import { LoadingScreen, DeleteConfirmDialog, ConfirmDialog } from '../components/ui';
import ViewLayout2 from '../components/ui/ViewLayout2';
import StatBar from '../components/ui/StatBar';
import DataGrid from '../components/ui/DataGrid';
import DataCard from '../components/ui/DataCard';
import { CourseForm } from '../components/courses';
import { notifyError, notifySuccess } from '../services/notificationService';

// Tab Components
import DashboardOverviewTab from '../components/dashboard/DashboardOverviewTab';
import DashboardCoursesTab from '../components/dashboard/DashboardCoursesTab';
import DashboardTemplatesTab from '../components/dashboard/DashboardTemplatesTab';
import DashboardSessionsTab from '../components/dashboard/DashboardSessionsTab';

/**
 * DashboardView2 - Neue Dashboard View mit Unified Design System
 * 
 * Struktur:
 * - Header mit Titel und User-Actions
 * - Action Bar mit Tabs (Übersicht, Kurse, Vorlagen, Sessions)
 * - StatBar für schnelle Statistiken
 * - Tab Content je nach aktivem Tab
 */

const TABS = [
  { id: 'overview', label: 'Übersicht' },
  { id: 'courses', label: 'Kurse' },
  { id: 'templates', label: 'Vorlagen' },
  { id: 'sessions', label: 'Sessions' }
];

export default function DashboardView2() {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useUnifiedAuth();
  const { courses, loading: coursesLoading, createCourse, updateCourse, deleteCourse } = useCourses();
  const { templates } = useTemplates();
  const { instances, getActiveInstances, startInstance, endInstance, deleteInstance } = useInstances();
  
  // UI State
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [sessionToEnd, setSessionToEnd] = useState(null);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // Computed data
  const activeCourses = useMemo(() => 
    courses.filter(c => !c.archived), 
    [courses]
  );
  
  const activeInstances = useMemo(() => 
    getActiveInstances(), 
    [instances]
  );

  // Update tabs with counts
  const tabsWithCounts = useMemo(() => 
    TABS.map(tab => {
      if (tab.id === 'courses') return { ...tab, count: activeCourses.length };
      if (tab.id === 'templates') return { ...tab, count: templates.length };
      if (tab.id === 'sessions') return { ...tab, count: activeInstances.length };
      return tab;
    }), 
    [activeCourses.length, templates.length, activeInstances.length]
  );

  // Stats for StatBar
  const stats = [
    { value: activeCourses.length, label: 'Kurse', color: 'primary' },
    { value: templates.length, label: 'Vorlagen', color: 'secondary' },
    { value: activeInstances.length, label: 'Aktiv', color: 'success' }
  ];

  // Handlers
  const handleCreate = (type) => {
    switch(type) {
      case 'course':
        setShowCourseForm(true);
        break;
      case 'template':
        navigate('/template/new');
        break;
      case 'ai':
        navigate('/llm-generator');
        break;
      default:
        break;
    }
  };

  const handleCreateCourse = async (formData) => {
    setIsSubmitting(true);
    try {
      const newCourse = await createCourse(formData);
      setShowCourseForm(false);
      notifySuccess('Kurs erfolgreich erstellt!');
      // Optional: Navigate to new course
      // navigate(`/course/${newCourse.id}`);
    } catch (error) {
      notifyError('Fehler beim Erstellen des Kurses: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateCourse = async (formData) => {
    if (!editingCourse) return;
    
    setIsSubmitting(true);
    try {
      await updateCourse(editingCourse.id, formData);
      setEditingCourse(null);
      notifySuccess('Kurs erfolgreich aktualisiert!');
    } catch (error) {
      notifyError('Fehler beim Aktualisieren des Kurses: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    try {
      await deleteCourse(courseToDelete.id);
      setCourseToDelete(null);
      notifySuccess('Kurs erfolgreich gelöscht!');
    } catch (error) {
      notifyError('Fehler beim Löschen des Kurses: ' + error.message);
    }
  };

  const handleStartQuiz = async (templateId) => {
    try {
      const session = await startInstance(templateId);
      navigate(`/live/${session.id}`);
    } catch (error) {
      notifyError('Fehler beim Starten des Quiz: ' + error.message);
    }
  };

  const handleEndSession = async (sessionId) => {
    setSessionToEnd(sessionId);
  };

  const confirmEndSession = async () => {
    if (!sessionToEnd) return;
    try {
      await endInstance(sessionToEnd);
      setSessionToEnd(null);
      notifySuccess('Session erfolgreich beendet!');
    } catch (error) {
      notifyError('Fehler beim Beenden der Session: ' + error.message);
      setSessionToEnd(null);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    setSessionToDelete(sessionId);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    try {
      await deleteInstance(sessionToDelete);
      setSessionToDelete(null);
      notifySuccess('Session erfolgreich gelöscht!');
    } catch (error) {
      notifyError('Fehler beim Löschen der Session: ' + error.message);
      setSessionToDelete(null);
    }
  };

  // Filter data based on search
  const filteredCourses = useMemo(() => {
    if (!searchQuery) return activeCourses;
    const query = searchQuery.toLowerCase();
    return activeCourses.filter(c => 
      c.name?.toLowerCase().includes(query) ||
      c.description?.toLowerCase().includes(query)
    );
  }, [activeCourses, searchQuery]);

  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.name?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // Loading state - wait for auth and courses
  if (authLoading || coursesLoading) {
    return <LoadingScreen message="Lade Dashboard..." />;
  }

  // Not authenticated - redirect to login
  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <ViewLayout2
      title="Dashboard"
      tabs={tabsWithCounts}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      searchPlaceholder="Suche..."
      onSearch={setSearchQuery}
      primaryAction={{
        label: 'Erstellen',
        dropdown: [
          { label: 'Neuer Kurs', onClick: () => handleCreate('course') },
          { label: 'Neue Vorlage', onClick: () => handleCreate('template') },
          { label: 'Mit KI generieren', onClick: () => handleCreate('ai') }
        ]
      }}
      secondaryActions={
        userProfile?.role === USER_ROLES.ADMIN 
          ? [{ label: 'Admin', onClick: () => navigate('/admin/dashboard') }]
          : []
      }
    >
      {/* Stats Bar */}
      <StatBar stats={stats} />
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <DashboardOverviewTab 
          courses={filteredCourses}
          templates={templates}
          activeSessions={activeInstances}
          allInstances={instances}
          onStartQuiz={handleStartQuiz}
          onEndSession={handleEndSession}
          onDeleteSession={handleDeleteSession}
          onCreateCourse={() => setShowCourseForm(true)}
          onCreateTemplate={() => navigate('/template/new')}
          onOpenAIGenerator={() => navigate('/llm-generator')}
        />
      )}
      
      {activeTab === 'courses' && (
        <DashboardCoursesTab 
          courses={filteredCourses}
          templates={templates}
          instances={instances}
          onEdit={(course) => setEditingCourse(course)}
          onDelete={(course) => setCourseToDelete(course)}
          onCreateCourse={() => setShowCourseForm(true)}
        />
      )}
      
      {activeTab === 'templates' && (
        <DashboardTemplatesTab 
          templates={filteredTemplates}
          courses={courses}
          onStartQuiz={handleStartQuiz}
        />
      )}
      
      {activeTab === 'sessions' && (
        <DashboardSessionsTab 
          instances={instances}
          activeInstances={activeInstances}
          onEndSession={handleEndSession}
          onDeleteSession={handleDeleteSession}
        />
      )}

      {/* Course Form Modal */}
      {(showCourseForm || editingCourse) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {editingCourse ? 'Kurs bearbeiten' : 'Neuen Kurs erstellen'}
              </h2>
              <CourseForm
                initialData={editingCourse}
                onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse}
                onCancel={() => {
                  setShowCourseForm(false);
                  setEditingCourse(null);
                }}
                isLoading={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Course Confirmation */}
      <DeleteConfirmDialog
        isOpen={!!courseToDelete}
        onClose={() => setCourseToDelete(null)}
        onConfirm={confirmDeleteCourse}
        itemName={courseToDelete?.name || 'Kurs'}
      />

      {/* End Session Confirmation */}
      <ConfirmDialog
        isOpen={!!sessionToEnd}
        onClose={() => setSessionToEnd(null)}
        onConfirm={confirmEndSession}
        title="Session beenden"
        message="Möchtest du diese Session wirklich beenden? Alle Teilnehmer werden von der Session getrennt."
        confirmLabel="Session beenden"
        variant="warning"
      />

      {/* Delete Session Confirmation */}
      <ConfirmDialog
        isOpen={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onConfirm={confirmDeleteSession}
        title="Session löschen"
        message="Möchtest du diese Session wirklich löschen? Alle Ergebnisse und Teilnehmerdaten werden unwiderruflich gelöscht."
        confirmLabel="Session löschen"
        variant="danger"
      />
    </ViewLayout2>
  );
}
